import { Controller } from "@hotwired/stimulus"

// Procore → ConsLog RFIs and Submittals on the Procore card (Colin Simons /
// Journey Construction, 2026-09-30). Saves a switch, starts a sync, keeps the
// status line fresh while a sync runs, and edits the type / status matches
// (Phelipe 2026-10-01) — the server sends the section back re-rendered, so
// nothing reloads the page.
export default class extends Controller {
  static values = {
    statusUrl: String,
    syncUrl: String,
    matchesUrl: String,
    csrfToken: String,
    editable: Boolean,
    syncing: Boolean,
    messages: Object,
    // "details" inside the Procore pop-up on the project page (2026-10-06):
    // the server sends this section back in the pop-up's layout.
    view: String,
    // "Try again" on changes two-way sync couldn't send to Procore.
    retryUrl: String,
    // Possible duplicates (2026-10-06): merge, keep both, look again.
    mergeUrl: String,
    notDuplicateUrl: String,
    findDuplicatesUrl: String
  }

  static targets = ["toggle", "count", "status", "syncBtn", "error", "modal", "matchSection", "statusSelect", "mergeModal",
    "confirm", "confirmTitle", "confirmBody", "confirmButton"]

  connect() {
    if (this.syncingValue) this._poll()
  }

  disconnect() {
    clearTimeout(this._timer)
    this._closeConfirm()
  }

  // Ticking or unticking RFIs / Submittals asks first (Phelipe 2026-10-06):
  // the box goes back until the contractor reads what that does and confirms.
  toggle(event) {
    const input = event.target
    const turningOn = input.checked
    if (!this.hasConfirmTarget) return this._saveToggle(input, turningOn)

    input.checked = !turningOn
    const confirm = this.messagesValue.confirm || {}
    const texts = (confirm[input.dataset.kind] || {})[turningOn ? "on" : "off"] || {}
    let body = texts.body || ""
    if (!turningOn && input.dataset.twoWay === "true" && confirm.two_way_off) body += ` ${confirm.two_way_off}`
    this.confirmTitleTarget.textContent = texts.title || ""
    this.confirmBodyTarget.textContent = body
    this.confirmButtonTarget.textContent = texts.button || ""
    this._pending = { input, turningOn }
    this.confirmTarget.hidden = false
    // Escape cancels this before the Procore pop-up under it sees the key.
    this._onConfirmKey = (e) => {
      if (e.key !== "Escape") return
      e.preventDefault()
      e.stopPropagation()
      this.cancelToggle()
    }
    window.addEventListener("keydown", this._onConfirmKey, true)
    this.confirmButtonTarget.focus()
  }

  confirmToggle(event) {
    event?.preventDefault()
    const pending = this._pending
    this._closeConfirm()
    if (!pending) return
    pending.input.checked = pending.turningOn
    this._saveToggle(pending.input, pending.turningOn)
  }

  cancelToggle(event) {
    event?.preventDefault()
    const input = this._pending?.input
    this._closeConfirm()
    input?.focus()
  }

  _closeConfirm() {
    this._pending = null
    if (this.hasConfirmTarget) this.confirmTarget.hidden = true
    if (this._onConfirmKey) window.removeEventListener("keydown", this._onConfirmKey, true)
    this._onConfirmKey = null
  }

  async _saveToggle(input, turningOn) {
    input.disabled = true
    try {
      const data = await this._request("PATCH", this.statusUrlValue, { [input.dataset.field]: turningOn })
      this._render(data)
      this._showToast(turningOn ? this.messagesValue.turned_on : this.messagesValue.turned_off, "notice")
      if (data.syncing) this._poll()
      else this._refresh()
    } catch (e) {
      input.checked = !turningOn
      this._showToast(e.message || this.messagesValue.failed, "alert")
    } finally {
      input.disabled = !this.editableValue
    }
  }

  async syncNow(event) {
    event.preventDefault()
    if (!this.hasSyncBtnTarget) return
    this.syncBtnTarget.disabled = true
    try {
      const data = await this._request("POST", this.syncUrlValue)
      this._render(data)
      this._showToast(this.messagesValue.sync_started, "notice")
      this._poll()
    } catch (e) {
      this._showToast(e.message || this.messagesValue.failed, "alert")
    } finally {
      if (this.hasSyncBtnTarget) this.syncBtnTarget.disabled = false
    }
  }

  // ── Sync direction (Phelipe 2026-10-06) ───────────────────────────

  // One way (Procore → ConsLog) or both ways, per kind. The server sends the
  // pop-up back with the new direction, what goes back and as whom.
  async setDirection(event) {
    event.preventDefault()
    const button = event.currentTarget
    if (button.getAttribute("aria-pressed") === "true") return
    const twoWay = button.dataset.twoWay === "true"
    button.disabled = true
    try {
      const data = await this._request("PATCH", this.statusUrlValue, { [`${button.dataset.kind}s_two_way`]: twoWay })
      this._showToast(twoWay ? this.messagesValue.two_way_on : this.messagesValue.two_way_off, "notice")
      this._swap(data.html)
    } catch (e) {
      this._showToast(e.message || this.messagesValue.failed, "alert")
      button.disabled = false
    }
  }

  // "Try again" on the changes Procore didn't take.
  async retryPushes(event) {
    event.preventDefault()
    const button = event.currentTarget
    button.disabled = true
    try {
      const data = await this._request("POST", this.retryUrlValue, { kind: button.dataset.kind })
      this._showToast(data.message, "notice")
      this._swap(data.html)
    } catch (e) {
      this._showToast(e.message || this.messagesValue.failed, "alert")
      button.disabled = false
    }
  }

  // The pop-up was opened: show what changed since the page loaded (new
  // items, what two-way sync sent).
  refreshNow() {
    if (!this.syncingValue) this._refresh()
  }

  // ── Possible duplicates (2026-10-06) ──────────────────────────────

  // Merge asks first: which Procore item goes into which ConsLog item.
  openMerge(event) {
    event.preventDefault()
    const button = event.currentTarget
    const row = button.closest("[data-duplicate-row]")
    const select = row?.querySelector("[data-keep-select]")
    const keepEl = row?.querySelector("[data-keep-id]")
    const keepId = select ? select.value : keepEl?.dataset.keepId
    const into = select ? select.options[select.selectedIndex]?.text : keepEl?.dataset.keepTitle
    const modal = this.mergeModalTargets.find((el) => el.dataset.mergeKind === button.dataset.kind) || this.mergeModalTargets[0]
    if (!modal || !keepId) return
    this._merge = { link_id: button.dataset.linkId, keep_id: keepId }
    const text = modal.querySelector("[data-merge-text]")
    if (text) text.textContent = (text.dataset.template || "").replace(/%\{from\}/g, button.dataset.from || "").replace(/%\{into\}/g, into || "")
    modal.hidden = false
  }

  cancelMerge(event) {
    event?.preventDefault()
    this.mergeModalTargets.forEach((el) => { el.hidden = true })
    this._merge = null
  }

  async confirmMerge(event) {
    event.preventDefault()
    const button = event.currentTarget
    if (!this._merge) return
    button.disabled = true
    try {
      const data = await this._request("POST", this.mergeUrlValue, this._merge)
      this._showToast(data.message, "notice")
      this._swap(data.html)
    } catch (e) {
      this._showToast(e.message || this.messagesValue.failed, "alert")
      button.disabled = false
    }
  }

  // "Keep both": not duplicates, never listed again.
  async notDuplicate(event) {
    event.preventDefault()
    const button = event.currentTarget
    button.disabled = true
    try {
      const data = await this._request("PATCH", this.notDuplicateUrlValue, { link_id: button.dataset.linkId })
      this._showToast(data.message, "notice")
      this._swap(data.html)
    } catch (e) {
      this._showToast(e.message || this.messagesValue.failed, "alert")
      button.disabled = false
    }
  }

  async findDuplicates(event) {
    event.preventDefault()
    const button = event.currentTarget
    button.disabled = true
    try {
      const data = await this._request("POST", this.findDuplicatesUrlValue, { kind: button.dataset.kind })
      this._showToast(data.message, "notice")
      this._swap(data.html)
    } catch (e) {
      this._showToast(e.message || this.messagesValue.failed, "alert")
      button.disabled = false
    }
  }

  // ── Type and status matches ────────────────────────────────────────

  // The pencil after RFIs / Submittals opens that kind's matching window
  // (2026-10-06); Review on the Procore card opens its one window.
  openMatches(event) {
    event?.preventDefault()
    const kind = event?.currentTarget?.dataset?.kind
    const windows = this.modalTargets.filter((el) => !el.dataset.mergeKind)
    const modal = (kind && windows.find((el) => el.dataset.kind === kind)) || windows[0]
    if (modal) modal.hidden = false
  }

  closeMatches(event) {
    event?.preventDefault()
    this.modalTargets.forEach((el) => { el.hidden = true })
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // Creating the missing "RFI" / "Submittal" type is the confirmation step:
  // nothing is added to the owner's types until this click.
  async createType(event) {
    event.preventDefault()
    await this._saveMatches({ kind: event.currentTarget.dataset.kind, type_id: "new" }, event.currentTarget)
  }

  async pickType(event) {
    const select = event.currentTarget
    if (!select.value) return
    const open = this._openModal()
    await this._saveMatches({ kind: select.dataset.kind, type_id: select.value }, select, { reopen: open ? (open.dataset.kind || true) : false })
  }

  async saveMatches(event) {
    event.preventDefault()
    const button = event.currentTarget
    // Only the window Save was pressed in (one kind in the pop-up, both on
    // the Procore card).
    const dialog = button.closest("[data-procore-items-target~='modal']")
    let last = null
    for (const section of this.matchSectionTargets.filter((el) => !dialog || dialog.contains(el))) {
      const statuses = {}
      section.querySelectorAll("[data-procore-items-target='statusSelect']").forEach((select) => {
        statuses[select.dataset.word] = select.value
      })
      last = await this._saveMatches({ kind: section.dataset.kind, statuses }, button, { quiet: true, swap: false })
      if (!last) return
    }
    if (last) {
      this._showToast(last.message || this.messagesValue.failed, "notice")
      this._swap(last.html)
    }
  }

  async _saveMatches(body, control, { reopen = false, quiet = false, swap = true } = {}) {
    if (control) control.disabled = true
    try {
      const data = await this._request("PATCH", this.matchesUrlValue, body)
      if (!quiet && data.message) this._showToast(data.message, "notice")
      if (swap) this._swap(data.html, { reopen })
      return data
    } catch (e) {
      this._showToast(e.message || this.messagesValue.failed, "alert")
      return null
    } finally {
      if (control) control.disabled = false
    }
  }

  _openModal() {
    return this.modalTargets.find((el) => !el.hidden) || null
  }

  _modalHidden() {
    return !this._openModal()
  }

  // Puts the re-rendered section in place of this one; Stimulus connects the
  // new copy (which starts polling again if a sync is running).
  _swap(html, { reopen = false } = {}) {
    if (!html) return
    clearTimeout(this._timer)
    const holder = document.createElement("div")
    holder.innerHTML = html.trim()
    const fresh = holder.firstElementChild
    if (!fresh) return
    this.element.replaceWith(fresh)
    if (reopen) {
      // The same kind's window when it was one of the pop-up's.
      const modal = (typeof reopen === "string" && fresh.querySelector(`[data-procore-items-target~='modal'][data-kind='${reopen}']`)) ||
        fresh.querySelector("[data-procore-items-target~='modal']")
      if (modal) modal.hidden = false
    }
    // The Procore pop-up brings back the tab that was open.
    fresh.dispatchEvent(new CustomEvent("procore-items:swapped", { bubbles: true }))
  }

  // ── Status line ────────────────────────────────────────────────────

  // While a sync runs, re-read the status every 5 seconds (up to 5 minutes),
  // then bring in the finished section (counts, matches, anything waiting).
  _poll() {
    clearTimeout(this._timer)
    let polls = 0
    const tick = async () => {
      polls += 1
      try {
        const data = await this._request("GET", this.statusUrlValue)
        this._render(data)
        if (data.syncing && polls < 60) this._timer = setTimeout(tick, 5000)
        else if (!data.syncing) this._refresh()
      } catch (_e) {
        // A status read failing is harmless; the next page load shows it.
      }
    }
    this._timer = setTimeout(tick, 3000)
  }

  async _refresh() {
    try {
      const data = await this._request("GET", `${this.statusUrlValue}?html=1`)
      if (this._modalHidden()) this._swap(data.html)
    } catch (_e) {
      // Keep what's on screen.
    }
  }

  _render(data) {
    this.toggleTargets.forEach((input) => {
      const on = input.dataset.kind === "rfi" ? data.rfis_enabled : data.submittals_enabled
      if (typeof on === "boolean") input.checked = on
    })

    this.countTargets.forEach((el) => {
      const count = (data.counts || {})[el.dataset.kind] || 0
      el.textContent = (this.messagesValue.count || "%{count}").replace("%{count}", count)
      el.hidden = count === 0
    })

    if (this.hasStatusTarget) {
      const anyOn = data.rfis_enabled || data.submittals_enabled
      if (data.syncing) this.statusTarget.textContent = this.messagesValue.syncing
      else if (data.last_synced_label) this.statusTarget.textContent = data.last_synced_label
      else this.statusTarget.textContent = anyOn ? this.messagesValue.never_synced : ""
    }

    if (this.hasSyncBtnTarget) this.syncBtnTarget.hidden = !(data.rfis_enabled || data.submittals_enabled)

    if (this.hasErrorTarget) {
      this.errorTarget.textContent = data.error || ""
      this.errorTarget.hidden = !data.error
    }
  }

  async _request(method, url, body = null) {
    if (this.viewValue) {
      if (method === "GET") url += `${url.includes("?") ? "&" : "?"}view=${encodeURIComponent(this.viewValue)}`
      else body = { ...(body || {}), view: this.viewValue }
    }
    const response = await fetch(url, {
      method,
      headers: {
        "X-CSRF-Token": this.csrfTokenValue,
        "Accept": "application/json",
        "Content-Type": "application/json"
      },
      body: body ? JSON.stringify(body) : null
    })
    let data = {}
    try { data = await response.json() } catch (_e) { data = {} }
    if (!response.ok || data.ok === false) throw new Error(data.error || this.messagesValue.failed)
    return data
  }

  _showToast(message, type = "notice") {
    if (!message) return
    const cssType = type === "alert" ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
